import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminThemeContext = createContext();

export const AdminThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('adminTheme');
    return saved ? saved === 'dark' : false; // Default to light mode
  });

  useEffect(() => {
    localStorage.setItem('adminTheme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Apply/remove the 'dark' class to document element for Tailwind dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Theme classes for consistent styling across admin pages
  const theme = {
    isDarkMode,
    // Layout
    bg: isDarkMode ? 'bg-slate-900' : 'bg-gray-100',
    // Cards and containers
    card: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    cardHover: isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50',
    // Text colors
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    textMuted: isDarkMode ? 'text-slate-400' : 'text-gray-600',
    textSubtle: isDarkMode ? 'text-slate-500' : 'text-gray-500',
    // Headers
    heading: isDarkMode ? 'text-white' : 'text-gray-900',
    subheading: isDarkMode ? 'text-slate-400' : 'text-gray-600',
    // Table
    tableHeader: isDarkMode ? 'bg-slate-800/80 text-slate-300' : 'bg-gray-100 text-gray-700',
    tableRow: isDarkMode ? 'border-slate-700' : 'border-gray-200',
    tableCell: isDarkMode ? 'text-slate-300' : 'text-gray-700',
    tableCellMuted: isDarkMode ? 'text-slate-400' : 'text-gray-500',
    // Calendar grid
    calendarBg: isDarkMode ? 'bg-slate-800' : 'bg-white',
    calendarBorder: isDarkMode ? 'border-slate-700' : 'border-gray-200',
    calendarRowBorder: isDarkMode ? 'border-slate-700/50' : 'border-gray-200',
    calendarTimeBg: isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50',
    calendarTimeText: isDarkMode ? 'text-slate-400' : 'text-gray-600',
    calendarSlotBorder: isDarkMode ? 'border-slate-600/50' : 'border-gray-300',
    calendarSlotHover: isDarkMode ? 'hover:border-emerald-500/50 hover:bg-emerald-500/10' : 'hover:border-emerald-500 hover:bg-emerald-50',
    calendarPast: isDarkMode ? 'bg-slate-800/50 border-slate-700/30' : 'bg-gray-100 border-gray-200',
    calendarPastText: isDarkMode ? 'text-slate-600' : 'text-gray-400',
    // Buttons
    btnPrimary: isDarkMode ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-600 hover:bg-emerald-700',
    btnSecondary: isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    btnOutline: isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100',
    // Inputs
    input: isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400',
    // Selection bar
    selectionBar: isDarkMode ? 'bg-emerald-500/20 border-emerald-500/50' : 'bg-emerald-50 border-emerald-300',
    selectionText: isDarkMode ? 'text-emerald-400' : 'text-emerald-700',
    selectionTextMuted: isDarkMode ? 'text-emerald-300' : 'text-emerald-600',
    // Stats cards
    statCard: isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm',
    statLabel: isDarkMode ? 'text-slate-400' : 'text-gray-500',
    statValue: isDarkMode ? 'text-white' : 'text-gray-900',
    // Modal
    modalBg: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    modalOverlay: 'bg-black/50',
    // Dropdown/Popover
    popover: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    // Badges/Status
    badgeSuccess: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700',
    badgeWarning: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700',
    badgeDanger: isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700',
    badgeInfo: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700',
  };

  return (
    <AdminThemeContext.Provider value={{ isDarkMode, toggleTheme, theme }}>
      {children}
    </AdminThemeContext.Provider>
  );
};

export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within an AdminThemeProvider');
  }
  return context;
};
